import { Base } from './base'
import { Device } from '@/models/device'
import { PlcDevice } from '@/models/plc-device'
import { PlcData } from '@/models/plc-data'
import { PlcDataFunc } from '@/models/func'
import { Interface } from '@/models/interface'
import { AnalogDevice } from '@/models/analog-device'
import { ModbusDevice } from '@/models/modbus-device'
import { ModbusData } from '@/models/modbus-data'
import { ModbusDataPoint } from '@/models/modbus-data-point'
import { Dlt645Device } from '@/models/dlt645-device'
import { Dlt645Data } from '@/models/dlt645-data'
import Joi from 'joi'
import { newGuid } from '../../../utils'
import { DB, DBVersion } from '@/extensions/db'
import { download } from '@/extensions/download'

export class DeviceTree extends Base {
  constructor (vueComponent) {
    super(vueComponent)

    this._device = new Device()
    this._plcDevice = new PlcDevice()
    this._plcData = new PlcData()
    this._plcDataFunc = new PlcDataFunc()
    this._interface = new Interface()
    this._analogDevice = new AnalogDevice()
    this._modbusDevice = new ModbusDevice()
    this._modbusData = new ModbusData()
    this._modbusDataPoint = new ModbusDataPoint()
    this._dlt645Device = new Dlt645Device()
    this._dlt645Data = new Dlt645Data()

    this._operationMap = {
      plcDeviceRoot: {
        add: this._addPlcDevice
      },
      analogDeviceRoot: {
        add: this._addAnalogDevice
      },
      modbusDeviceRoot: {
        add: this._addModbusDevice
      },
      dlt645DeviceRoot: {
        add: this._addDlt645Device
      },
      plcDevice: {
        click: this._inspectPlcDevice,
        add: this._addPlcDeviceData,
        edit: this._editPlcDevice,
        remove: this._removePlcDevice
      },
      plcData: {
        click: this._inspectPlcData,
        edit: this._editPlcData,
        remove: this._removePlcData,
        add: async plcData => {
          await this._addFunc(plcData.id, 'plcData')
        }
      },
      plcDataFunc: {
        click: this._inspectPlcDataFunc,
        edit: this._editPlcDataFunc,
        remove: this._removePlcDataFunc,
        copy: this._copyFunc
      },
      analogDeviceFunc: {
        click: this._inspectPlcDataFunc,
        edit: this._editPlcDataFunc,
        remove: this._removePlcDataFunc,
        copy: this._copyFunc
      },
      modbusDataPointFunc: {
        click: this._inspectPlcDataFunc,
        edit: this._editPlcDataFunc,
        remove: this._removePlcDataFunc,
        copy: this._copyFunc
      },
      dlt645DataFunc: {
        click: this._inspectPlcDataFunc,
        edit: this._editPlcDataFunc,
        remove: this._removePlcDataFunc,
        copy: this._copyFunc
      },
      analogDevice: {
        click: this._inspectAnalogDevice,
        edit: this._editAnalogDevice,
        remove: this._removeAnalogDevice,
        add: async device => {
          await this._addFunc(device.id, 'analogDevice')
        }
      },
      modbusDevice: {
        click: this._inspectModbusDevice,
        edit: this._editModbusDevice,
        remove: this._removeModbusDevice,
        add: this._addModbusDeviceData,
        copy: this._copyModbusDevice,
        export: this._exportModbusDevice
      },
      dlt645Device: {
        click: this._inspectDlt645Device,
        edit: this._editDlt645Device,
        remove: this._removeDlt645Device,
        add: this._addDlt645DeviceData
      },
      modbusData: {
        click: this._inspectModbusData,
        edit: this._editModbusData,
        remove: this._removeModbusData,
        add: this._addModbusDeviceDataPonit,
        copy: this._copyModbusData
      },
      modbusDataPoint: {
        click: this._inspectModbusDataPoint,
        edit: this._editModbusDataPoint,
        remove: this._removeModbusDataPoint,
        add: async item => {
          await this._addFunc(item.id, 'modbusDataPoint')
        },
        copy: this._copyModbusDataPoint
      },
      dlt645Data: {
        click: this._inspectDlt645Data,
        edit: this._editDlt645Data,
        remove: this._removeDlt645Data,
        add: async modbusData => {
          await this._addFunc(modbusData.id, 'dlt645Data')
        }
      }
    }

    this._addListeners()
  }

  async genTreeData () {
    let data = [
      {
        id: 'root-node1',
        name: 'PLC 设备',
        operations: [ 'add' ],
        class: 'plcDeviceRoot',
        children: await this._genPlcDeviceTreeData()
      },
      {
        id: 'root-node2',
        name: '模拟量设备',
        operations: [ 'add' ],
        class: 'analogDeviceRoot',
        children: await this._genAnalogDeviceTreeData()
      },
      {
        id: 'root-node3',
        name: 'Modbus 协议设备',
        class: 'modbusDeviceRoot',
        operations: [ 'add' ],
        children: await this._genModbusDeviceTreeData()
      },
      {
        id: 'root-node4',
        name: 'DLT645 协议设备',
        class: 'dlt645DeviceRoot',
        operations: [ 'add' ],
        children: await this._genDlt645DeviceTreeData()
      }
    ]

    return data
  }

  async processOperation (operation, data) {
    let cb = this._operationMap[data.class][operation]

    await cb.call(this, data)
  }

  async _inspectPlcDevice (plc) {
    let data = await this._plcDevice.get(plc.id)

    this._requestForm('plc-device', data, false)
  }

  async _editPlcDevice (plc) {
    let data = await this._plcDevice.get(plc.id)

    this._requestForm('plc-device', Object.assign({
      _id: plc.id
    }, data), true, 'update-plc-device')
  }

  async _removePlcDevice (plc) {
    await this._plcDevice.delete(plc.id)
    const datas = await DB.plcData.where({plcDeviceId: plc.id}).toArray()
    const deleteDatasPromise = datas.reduce((prev, item) => {
      prev.push(DB.func.where({parentId: item.id, parentType: 'plcData'}).delete())
      return prev
    }, [])
    await Promise.all(deleteDatasPromise)
    await DB.plcData.where({plcDeviceId: plc.id}).delete()
    this.alertSuccess('删除成功')
  }

  async _addPlcDevice () {
    this._requestForm('plc-device', {
      name: '',
      manufacturer: '',
      model: '',
      connectionType: '',
      interfaceType: '',
      interface: '',
      args: {}
    })
  }

  async _addPlcDeviceData (plc) {
    const plcInfo = await this._plcDevice.get(plc.id)

    let note = ''
    try {
      note = this._vueComponent.$store.state.Config
        .plc[plcInfo.manufacturer][plcInfo.model].dataNote
    } catch (err) {
    }

    return this._requestForm('plc-data', {
      _id: plc.id,
      address: '',
      type: '',
      note
    })
  }

  async _inspectPlcData (plcData) {
    let data = await this._plcData.get(plcData.id)

    await this._requestForm('plc-data', data, false)
  }

  async _editPlcData (plcData) {
    let data = await this._plcData.get(plcData.id)

    return this._requestForm('plc-data', Object.assign(data, {
      _id: plcData.id
    }), true, 'update-plc-data')
  }

  async _removePlcData (plcData) {
    await this._plcData.delete(plcData.id)
    await DB.func.where({parentId: plcData.id, parentType: 'plcData'}).delete()
    this.alertSuccess('删除成功')
  }

  async _addFunc (parentId, parentType) {
    let _id = {
      parentId,
      parentType
    }

    return this._requestForm('func', {
      _id,
      type: '',
      args: {}
    })
  }

  async _inspectPlcDataFunc (func) {
    let data = await this._plcDataFunc.get(func.id)

    await this._requestForm('func', data, false)
  }

  async _editPlcDataFunc (func) {
    let data = await this._plcDataFunc.get(func.id)

    return this._requestForm('func', Object.assign({
      _id: func.id
    }, data), true, 'updateFunc')
  }

  async _removePlcDataFunc (func) {
    await this._plcDataFunc.delete(func.id)

    this.alertSuccess('删除成功')
  }

  async _adcInterfaceList () {
    let list = await this._interface.all('ADC')
    list = list.map(iface => {
      return {
        key: iface.name,
        value: iface.id
      }
    })

    return list
  }

  async _netInterfaceList () {
    let list = await this._interface.all('NET')

    return list.map(iface => {
      return {
        key: iface.name,
        value: iface.id
      }
    })
  }

  async _rs485InterfaceList () {
    let list = await this._interface.all('RS485')

    return list.map(iface => {
      return {
        key: iface.name,
        value: iface.id
      }
    })
  }

  async _addAnalogDevice (device) {
    return this._requestForm('analog-device', {
      name: '',
      interfaceType: 'ADC',
      interface: '',
      interfaceInstanceList: await this._adcInterfaceList()
    })
  }

  async _inspectAnalogDevice (device) {
    let data = await this._analogDevice.get(device.id)

    await this._requestForm('analog-device', Object.assign({
      interfaceInstanceList: await this._adcInterfaceList()
    }, data), false)
  }

  async _editAnalogDevice (device) {
    let data = await this._analogDevice.get(device.id)

    return this._requestForm('analog-device', Object.assign({
      _id: device.id,
      interfaceInstanceList: await this._adcInterfaceList()
    }, data), true, 'update-analog-device')
  }

  async _removeAnalogDevice (device) {
    await this._analogDevice.delete(device.id)
    await DB.func.where({parentId: device.id, parentType: 'analogDevice'}).delete()
    this.alertSuccess('删除成功')
  }

  async _addModbusDevice (device) {
    return this._requestForm('modbus-device', {
      name: '',
      address: '',
      interfaceType: '',
      interface: ''
    })
  }

  async _inspectModbusDevice (device) {
    let data = await this._modbusDevice.get(device.id)
    await this._requestForm('modbus-device', data, false)
  }

  async _editModbusDevice (device) {
    let data = await this._modbusDevice.get(device.id)

    return this._requestForm('modbus-device', Object.assign({_id: device.id}, data), true, 'update-modbus-device')
  }

  async _exportModbusDevice ({id}) {
    const datas = await DB.modbusData.where({modbusDeviceId: id}).toArray()
    const deviceDatas = await Promise.all(datas.map(async ({address, cmdType, regNum, id}) => {
      let points = await DB.modbusDataPoint.where({modbusDataId: id}).toArray()
      points = await Promise.all(points.map(async ({id, offset, regNum}) => {
        let func = await DB.func.where({parentId: id, parentType: 'modbusDataPoint'}).toArray()
        func = func.sort((a, b) => a.order - b.order).map(({type, args}) => ({type, args}))
        return {
          offset,
          regNum,
          function: func
        }
      }))
      return {address, cmdType, regNum, points}
    }))
    let {name, protocolType, interfaceType} = await DB.modbusDevice.get(id)
    const device = {
      version: DBVersion,
      model: name,
      type: 'modbusDevice',
      deviceArgs: { protocolType, interfaceType },
      data: deviceDatas
    }
    download(`${name}-modbusDevice-export.json`, JSON.stringify(device))
  }

  async _removeModbusDevice (device) {
    await this._modbusDevice.delete(device.id)
    const modbusDataCollection = DB.modbusData.where({modbusDeviceId: device.id})
    const datas = await modbusDataCollection.toArray()

    const deleteDatasPromise = datas.map(async item => {
      const pointCollection = DB.modbusDataPoint.where({modbusDataId: item.id})
      const points = await pointCollection.toArray()
      await Promise.all(points.map(async point => {
        await DB.func.where({parentId: point.id, parentType: 'modbusDataPoint'}).delete()
      }))
      await pointCollection.delete()
    })
    await Promise.all(deleteDatasPromise)
    await modbusDataCollection.delete()
    this.alertSuccess('删除成功')
  }

  async _addModbusDeviceData (device) {
    return this._requestForm('modbus-data', {
      _id: device.id,
      cmdType: '',
      address: '',
      regNum: 1
    })
  }

  async _addModbusDeviceDataPonit (deviceData) {
    return this._requestForm('modbus-data-point', {
      _id: deviceData.id,
      offset: 0,
      regNum: 1
    })
  }

  _getCopyName (nameList, oldName) {
    const tempName = `${oldName} copy`
    const [lastCopyItem] = nameList.map(name => {
      const numberField = name.slice(tempName.length + 1)
      const order = numberField ? Number.parseInt(numberField, 10) : 0
      return {
        name,
        numberField,
        order
      }
    }).filter(({name, numberField, order}) => {
      if (name === tempName) {
        return true
      } else {
        return name.indexOf(tempName) === 0 && order.toString() === numberField && order > 0
      }
    }).sort((a, b) => b.order - a.order)

    if (lastCopyItem) {
      return `${tempName} ${lastCopyItem.order + 1}`
    } else {
      return tempName
    }
  }

  async _copyModbusDevice (device) {
    const copyItem = await DB.modbusDevice.get({id: device.id})
    const allItems = await DB.modbusDevice.toArray()
    copyItem.name = this._getCopyName(allItems.map(item => item.name), copyItem.name)

    const newID = newGuid()
    await this._copyModbusDeviceDatas(copyItem.id, newID)
    copyItem.id = newID
    const newDeviceId = newGuid()
    await DB.device.add({
      id: newDeviceId,
      type: 'modbus'
    })
    copyItem.deviceId = newDeviceId
    await DB.modbusDevice.add(copyItem)
  }

  async _copyModbusDeviceDatas (deviceId, newDeviceId) {
    const oldDatas = await DB.modbusData.where({modbusDeviceId: deviceId}).toArray()
    await Promise.all(oldDatas.map(async data => {
      const newDataId = newGuid()
      await this._copyModbusDeviceDataPoints(data.id, newDataId)
      await DB.modbusData.add(Object.assign(data, {id: newDataId, modbusDeviceId: newDeviceId}))
    }))
  }

  async _copyDataFuns (parentId, parentType, newParentId) {
    const oldFuns = await DB.func.where({ parentId, parentType }).toArray()
    const copyFuns = oldFuns.map(fun => Object.assign(fun, {
      id: newGuid(),
      parentId: newParentId
    }))
    return DB.func.bulkAdd(copyFuns)
  }

  async _copyModbusData (ModbusData) {
    const copyItem = await DB.modbusData.get({id: ModbusData.id})
    const newDataId = newGuid()
    await this._copyModbusDeviceDataPoints(ModbusData.id, newDataId)
    await DB.modbusData.add(Object.assign(copyItem, {id: newDataId}))
  }

  async _copyModbusDataPoint (point) {
    const copyItem = await DB.modbusDataPoint.get({id: point.id})
    const newPointId = newGuid()
    await this._copyDataFuns(point.id, 'modbusDataPoint', newPointId)
    await DB.modbusDataPoint.add(Object.assign(copyItem, {id: newPointId}))
  }

  async _copyModbusDeviceDataPoints (oldParent, newParent) {
    const oldPoints = await DB.modbusDataPoint.where({modbusDataId: oldParent}).toArray()
    await Promise.all(oldPoints.map(async point => {
      const newPointId = newGuid()
      await this._copyDataFuns(point.id, 'modbusDataPoint', newPointId)
      await DB.modbusDataPoint.add(Object.assign(point, {id: newPointId, modbusDataId: newParent}))
    }))
  }

  async _copyFunc (func) {
    const copyItem = await DB.func.get({ id: func.id })
    const brotherItems = await DB.func.where({parentId: copyItem.parentId, parentType: copyItem.parentType}).toArray()
    const maxOrder = Math.max(...brotherItems.map(item => item.order).concat(0))

    await DB.func.add(Object.assign(copyItem, {
      id: newGuid(),
      order: maxOrder + 1
    }))
  }

  async _inspectModbusData (modbusData) {
    let data = await this._modbusData.get(modbusData.id)

    await this._requestForm('modbus-data', data, false)
  }

  async _editModbusData (modbusData) {
    let data = await this._modbusData.get(modbusData.id)

    return this._requestForm('modbus-data', Object.assign({
      _id: modbusData.id
    }, data), true, 'update-modbus-data')
  }

  async _removeModbusData (modbusData) {
    await this._modbusData.delete(modbusData.id)
    const pointCollection = DB.modbusDataPoint.where({modbusDataId: modbusData.id})
    const points = await pointCollection.toArray()
    await Promise.all(points.map(async point => {
      await DB.func.where({parentId: point.id, parentType: 'modbusDataPoint'}).delete()
    }))
    await pointCollection.delete()
    this.alertSuccess('删除成功')
  }

  async _inspectModbusDataPoint (modbusDataPoint) {
    let data = await this._modbusDataPoint.get(modbusDataPoint.id)

    await this._requestForm('modbus-data-point', data, false)
  }

  async _editModbusDataPoint (modbusDataPoint) {
    let data = await this._modbusDataPoint.get(modbusDataPoint.id)

    return this._requestForm('modbus-data-point', Object.assign({
      _id: modbusDataPoint.id
    }, data), true, 'update-modbus-data-point')
  }

  async _removeModbusDataPoint (modbusDataPoint) {
    await DB.func.where({parentId: modbusDataPoint.id, parentType: 'modbusDataPoint'}).delete()
    await this._modbusDataPoint.delete(modbusDataPoint.id)
    this.alertSuccess('删除成功')
  }

  async _addDlt645Device () {
    return this._requestForm('dlt645-device', {
      name: '',
      address: '',
      interfaceType: 'RS485',
      interface: '',
      interfaceInstanceList: await this._rs485InterfaceList()
    })
  }

  async _inspectDlt645Device (device) {
    let data = await this._dlt645Device.get(device.id)

    await this._requestForm('dlt645-device', Object.assign({
      interfaceInstanceList: await this._rs485InterfaceList()
    }, data), false)
  }

  async _editDlt645Device (device) {
    let data = await this._dlt645Device.get(device.id)

    return this._requestForm('dlt645-device', Object.assign({
      _id: device.id,
      interfaceInstanceList: await this._rs485InterfaceList()
    }, data), true, 'update-dlt645-device')
  }

  async _removeDlt645Device (device) {
    await this._dlt645Device.delete(device.id)
    const datas = await DB.dlt645Data.where({dlt645DeviceId: device.id}).toArray()
    const deleteDatasPromise = datas.reduce((prev, item) => {
      prev.push(DB.func.where({parentId: item.id, parentType: 'dlt645Data'}).delete())
      return prev
    }, [])
    await Promise.all(deleteDatasPromise)
    await DB.dlt645Data.where({dlt645DeviceId: device.id}).delete()
    this.alertSuccess('删除成功')
  }

  async _addDlt645DeviceData (device) {
    return this._requestForm('dlt645-data', {
      _id: device.id,
      dataId: ''
    })
  }

  async _inspectDlt645Data (dlt645Data) {
    let data = await this._dlt645Data.get(dlt645Data.id)

    await this._requestForm('dlt645-data', data, false)
  }

  async _editDlt645Data (dlt645Data) {
    let data = await this._dlt645Data.get(dlt645Data.id)

    return this._requestForm('dlt645-data', Object.assign({
      _id: dlt645Data.id
    }, data), true, 'update-dlt645-data')
  }

  async _removeDlt645Data (dlt645Data) {
    await this._dlt645Data.delete(dlt645Data.id)
    await DB.func.where({parentId: dlt645Data.id, parentType: 'dlt645Data'}).delete()
    this.alertSuccess('删除成功')
  }

  async _genPlcDeviceTreeData () {
    let data = []

    for (let plc of await this._plcDevice.all()) {
      data.push({
        name: plc.name,
        operations: [ 'click', 'remove', 'edit', 'add' ],
        class: 'plcDevice',
        id: plc.id,
        children: await this._genPlcDataTreeData(plc.id)
      })
    }

    return data
  }

  async _genPlcDataTreeData (plcDeviceId) {
    return Promise.all((await this._plcData.plcId(plcDeviceId)).map(async (plcData, index) => {
      return {
        name: `${plcData.address}/${plcData.type}`,
        operations: [ 'click', 'remove', 'edit', 'add' ],
        class: 'plcData',
        id: plcData.id,
        children: await this._genFuncTreeData(plcData.id, 'plcData', 'plcDataFunc')
      }
    }))
  }

  async _genFuncTreeData (parentId, parentType, classname) {
    const funcs = await this._plcDataFunc.all(parentId, parentType)
    return funcs.sort((a, b) => a.order - b.order).map((func, index) => ({
      name: '#' + index + ' - ' + func.type,
      operations: [ 'click', 'remove', 'edit', 'copy' ],
      class: classname,
      id: func.id
    }))
  }

  async _genAnalogDeviceTreeData () {
    let list = []

    for (let device of await this._analogDevice.all()) {
      list.push({
        name: device.name,
        operations: [ 'click', 'remove', 'edit', 'add' ],
        class: 'analogDevice',
        id: device.id,
        children: await this._genFuncTreeData(device.id, 'analogDevice', 'analogDeviceFunc')
      })
    }

    return list
  }

  async _genModbusDeviceTreeData () {
    let list = []
    let devices = await this._modbusDevice.all()
    devices = devices.sort((a, b) => {
      if (a.name < b.name) {
        return -1
      } else {
        return 1
      }
    })
    for (let device of devices) {
      list.push({
        name: device.name,
        operations: [ 'click', 'remove', 'edit', 'copy', 'export', 'add' ],
        class: 'modbusDevice',
        id: device.id,
        children: await this._genModbusDeviceDataTreeData(device.id)
      })
    }
    return list
  }

  async _genModbusDeviceDataTreeData (modbusDeviceId) {
    return Promise.all((await this._modbusData.modbusId(modbusDeviceId)).map(async (modbusData, index) => {
      return {
        name: `${this._modbusTypeMapping(modbusData.cmdType)}/${modbusData.address}`,
        operations: [ 'click', 'remove', 'edit', 'copy', 'add' ],
        class: 'modbusData',
        id: modbusData.id,
        children: await this._genModbusDeviceDataPointTreeData(modbusData.id)
      }
    }))
  }

  async _genModbusDeviceDataPointTreeData (modbusDeviceDataId) {
    const points = await DB.modbusDataPoint.where({modbusDataId: modbusDeviceDataId}).toArray()
    const allPromise = points.map(async (point) => {
      return {
        name: `${point.offset}-${point.regNum}`,
        operations: [ 'click', 'remove', 'edit', 'copy', 'add' ],
        class: 'modbusDataPoint',
        id: point.id,
        children: await this._genFuncTreeData(point.id, 'modbusDataPoint', 'modbusDataPointFunc')
      }
    })
    return Promise.all(allPromise)
  }

  async _genDlt645DeviceTreeData () {
    let list = []

    for (let device of await this._dlt645Device.all()) {
      list.push({
        name: device.name,
        operations: [ 'click', 'remove', 'edit', 'add' ],
        class: 'dlt645Device',
        id: device.id,
        children: await this._genDlt645DeviceDataTreeData(device.id)
      })
    }

    return list
  }

  async _genDlt645DeviceDataTreeData (dlt645DeviceId) {
    return Promise.all((await this._dlt645Data.dlt645Id(dlt645DeviceId)).map(async (dlt645Data, index) => {
      return {
        name: dlt645Data.dataId,
        operations: [ 'click', 'remove', 'edit', 'add' ],
        class: 'dlt645Data',
        id: dlt645Data.id,
        children: await this._genFuncTreeData(dlt645Data.id, 'dlt645Data', 'dlt645DataFunc')
      }
    }))
  }

  _modbusTypeMapping (type) {
    return ({
      readCoils: 'FC1',
      readDiscreteInputs: 'FC2',
      readHoldingRegisters: 'FC3',
      readInputRegisters: 'FC4'
    })[type]
  }

  get _plcSchemaMap () {
    return {
      's7-200-tcp': {
        host: Joi.string().ip().required(),
        port: Joi.number().required()
      },
      's7': {
        host: Joi.string().ip().required(),
        port: Joi.number().required(),
        rack: Joi.number().required(),
        slot: Joi.number().required()
      },
      'mc': {
      },
      'bcnet-modbus-tcp': {
        plcModule: Joi.string().required(),
        host: Joi.string().ip().required(),
        port: Joi.number().required()
      },
      'omron-cpm-serial': {
      },
      /* FIXME: empty */
      'omron-fins-serial': {
      },
      'ge-snp-serial': {
        isSkipSendBreak: Joi.boolean()
      }
    }
  }

  _verifyPlcDevice (data) {
    const res = Joi.validate(data, {
      _id: Joi.string(),
      name: Joi.string().required(),
      manufacturer: Joi.string().required(),
      model: Joi.string().required(),
      connectionType: Joi.string().required(),
      interfaceType: Joi.string().required(),
      interface: Joi.string().required(),
      module: Joi.string().required(),
      args: Joi.object()
    })

    if (res.error) {
      throw res.error
    }

    const schema = this._plcSchemaMap[data.module]
    if (!schema) {
      throw new Error(`Invliad module: '${data.module}'`)
    }

    const argsRes = Joi.validate(data.args, schema)
    if (argsRes.error) {
      throw argsRes.error
    }

    res.value.args = argsRes.value

    return res.value
  }

  _verifyPlcData (data) {
    const res = Joi.validate(data, {
      _id: Joi.string(),
      address: Joi.string().required(),
      type: Joi.string().required()
    })

    if (res.error) {
      throw res.error
    }

    return res.value
  }

  _verifyFunc (data) {
    const res = Joi.validate(data, {
      _id: [ Joi.object(), Joi.string() ],
      type: Joi.string().required(),
      args: Joi.array().required()
    })

    if (res.error) {
      throw res.error
    }

    return res.value
  }

  _verifyAnalogDevice (data) {
    const res = Joi.validate(data, {
      _id: Joi.string(),
      name: Joi.string().required(),
      interfaceType: Joi.string().required(),
      interface: Joi.string().required(),
      module: Joi.string().required()
    })

    if (res.error) {
      throw res.error
    }

    return res.value
  }

  _verifyModbusDevice (data) {
    const isModbusTcp = data.protocolType === 'TCP'
    const validateObj = {
      _id: Joi.string(),
      name: Joi.string().required(),
      protocolType: Joi.string().required(),
      interfaceType: Joi.string().required(),
      interface: Joi.string().required(),
      module: Joi.string().required(),
      address: isModbusTcp ? Joi.string().ip().required() : Joi.number().required(),
      port: isModbusTcp ? Joi.number() : Joi.any(),
      nodeType: isModbusTcp ? Joi.string() : Joi.any(),
      unitId: isModbusTcp ? Joi.number() : Joi.any()
    }
    const res = Joi.validate(data, validateObj)

    if (res.error) {
      throw res.error
    }

    return res.value
  }

  _verifyModbusData (data) {
    const res = Joi.validate(data, {
      _id: Joi.string(),
      cmdType: Joi.string().required(),
      address: Joi.number().required(),
      regNum: Joi.number().required()
    })

    if (res.error) {
      throw res.error
    }

    return res.value
  }

  _verifyModbusDataPoint (data) {
    const res = Joi.validate(data, {
      _id: Joi.string(),
      offset: Joi.number().required(),
      regNum: Joi.number().required()
    })

    if (res.error) {
      throw res.error
    }

    return res.value
  }

  _verifyDlt645Device (data) {
    const res = Joi.validate(data, {
      _id: Joi.string(),
      name: Joi.string().required(),
      address: Joi.string().required(),
      interfaceType: Joi.string().required(),
      interface: Joi.string().required(),
      module: Joi.string().required()
    })

    if (res.error) {
      throw res.error
    }

    return res.value
  }

  _verifyDlt645Data (data) {
    const res = Joi.validate(data, {
      _id: Joi.string(),
      dataId: Joi.string().required()
    })

    if (res.error) {
      throw res.error
    }

    return res.value
  }

  async _addListeners () {
    /* add plc-device */
    this._addFormListener('plc-device', async data => {
      const value = this._verifyPlcDevice(data)

      await this._plcDevice.add(Object.assign(value))

      this.alertSuccess('保存成功')
    })

    /* edit plc-device */
    this._addFormListener('update-plc-device', async data => {
      const value = this._verifyPlcDevice(data)

      await this._plcDevice.update(value._id, value)

      this.alertSuccess('保存成功')
    })

    /* add plc-data */
    this._addFormListener('plc-data', async data => {
      const value = this._verifyPlcData(data)

      await this._plcData.add(Object.assign({
        plcDeviceId: value._id
      }, value))

      this.alertSuccess('保存成功')
    })

    /* edit plc-data */
    this._addFormListener('update-plc-data', async data => {
      const value = this._verifyPlcData(data)

      await this._plcData.update(value._id, value)

      this.alertSuccess('保存成功')
    })

    /* add func */
    this._addFormListener('func', async data => {
      const value = this._verifyFunc(data)

      this._plcDataFunc.add(Object.assign({
        parentId: value._id.parentId,
        parentType: value._id.parentType
      }, value))

      this.alertSuccess('保存成功')
    })

    /* update func */
    this._addFormListener('updateFunc', async data => {
      const value = this._verifyFunc(data)

      await this._plcDataFunc.update(value._id, value)

      this.alertSuccess('保存成功')
    })

    /* add analog-device */
    this._addFormListener('analog-device', async data => {
      const value = this._verifyAnalogDevice(data)

      await this._analogDevice.add(value)

      this.alertSuccess('保存成功')
    })

    /* edit analog-device */
    this._addFormListener('update-analog-device', async data => {
      const value = this._verifyAnalogDevice(data)

      await this._analogDevice.update(value._id, value)

      this.alertSuccess('保存成功')
    })

    /* add modbus-device */
    this._addFormListener('modbus-device', async data => {
      const value = this._verifyModbusDevice(data)

      await this._modbusDevice.add(value)

      this.alertSuccess('保存成功')
    })

    /* edit modbus-device */
    this._addFormListener('update-modbus-device', async data => {
      const value = this._verifyModbusDevice(data)

      await this._modbusDevice.update(value._id, value)

      this.alertSuccess('保存成功')
    })

    /* add modbus-data */
    this._addFormListener('modbus-data', async data => {
      const value = this._verifyModbusData(data)

      await this._modbusData.add(Object.assign({
        modbusDeviceId: value._id
      }, value))

      this.alertSuccess('保存成功')
    })

    /* edit modbus-data */
    this._addFormListener('update-modbus-data', async data => {
      const value = this._verifyModbusData(data)

      await this._modbusData.update(value._id, value)

      this.alertSuccess('保存成功')
    })

    /* add modbus-data-point */
    this._addFormListener('modbus-data-point', async data => {
      const value = this._verifyModbusDataPoint(data)

      await this._modbusDataPoint.add(Object.assign({
        modbusDataId: value._id
      }, value))

      this.alertSuccess('保存成功')
    })

    /* edit modbus-data-point */
    this._addFormListener('update-modbus-data-point', async data => {
      const value = this._verifyModbusDataPoint(data)

      await this._modbusDataPoint.update(value._id, value)

      this.alertSuccess('保存成功')
    })

    /* add dlt645-device */
    this._addFormListener('dlt645-device', async data => {
      const value = this._verifyDlt645Device(data)

      await this._dlt645Device.add(value)

      this.alertSuccess('保存成功')
    })

    /* edit dlt645-device */
    this._addFormListener('update-dlt645-device', async data => {
      const value = this._verifyDlt645Device(data)

      await this._dlt645Device.update(value._id, value)

      this.alertSuccess('保存成功')
    })

    /* add dlt645-data */
    this._addFormListener('dlt645-data', async data => {
      const value = this._verifyDlt645Data(data)

      await this._dlt645Data.add(Object.assign({
        dlt645DeviceId: value._id
      }, value))

      this.alertSuccess('保存成功')
    })

    /* edit dlt645-data */
    this._addFormListener('update-dlt645-data', async data => {
      const value = this._verifyDlt645Data(data)

      await this._dlt645Data.update(value._id, value)

      this.alertSuccess('保存成功')
    })
  }
}



// WEBPACK FOOTER //
// ./src/components/home/controllers/device.js